import {
  calculateDynamicDateRange,
  calculatePresetDateRange,
  validateCustomDateRange,
} from "@/utils/date/dateRange"
import type { DateRangePreset } from "@/utils/date/dateRangeTypes"

import type { GlobalDateRange } from "../types/core"
import type { DashboardDatePreferences } from "../types/dashboardConfig"
import {
  calculateRelativeDashboardDateRange,
  parseRelativeDashboardDateRange,
  serializeRelativeDashboardDateRange,
  type RelativeDashboardDateRange,
} from "./relativeDashboardDateRange"

interface ResolveDashboardDateRangeParams {
  defaultPreset: DateRangePreset
  savedPreferences?: DashboardDatePreferences
  urlEnd?: string | null
  urlStart?: string | null
  urlDashboardRange?: string | null
  calculateDynamicRange?: (
    amount: number,
    unit: "days" | "weeks" | "months",
  ) => GlobalDateRange
  calculatePresetRange?: (preset: DateRangePreset) => GlobalDateRange
}

interface BuildWidgetConfigHrefParams {
  currentTab?: string | null
  dashboardId: string
  organizationSlug: string
  widgetId?: string
  activeDateRange?: GlobalDateRange | null
  activeRelativeDateRange?: RelativeDashboardDateRange | null
}

interface BuildDashboardHrefParams {
  currentTab?: string | null
  dashboardId: string
  organizationSlug: string
  urlEnd?: string | null
  urlStart?: string | null
  urlDashboardRange?: string | null
}

export const getDashboardDisplayName = (
  dashboard:
    | {
        display_name?: string | null
        is_default?: boolean
      }
    | null
    | undefined,
): string | undefined => {
  if (!dashboard) {
    return undefined
  }

  if (dashboard.is_default) {
    return "Main"
  }

  return dashboard.display_name?.trim() || "Untitled"
}

const parseUrlDateRange = (
  urlStart?: string | null,
  urlEnd?: string | null,
): GlobalDateRange | null => {
  if (!urlStart || !urlEnd) {
    return null
  }

  const start = new Date(urlStart)
  const end = new Date(urlEnd)
  if (
    !validateCustomDateRange(start, end, "fixed", {
      allowSharedEndOfDay: true,
    }).isValid
  ) {
    return null
  }

  return { start, end }
}

export const resolveDashboardDateRangeOverride = ({
  defaultPreset,
  savedPreferences,
  urlEnd,
  urlStart,
  urlDashboardRange,
  calculateDynamicRange = calculateDynamicDateRange,
  calculatePresetRange = calculatePresetDateRange,
}: ResolveDashboardDateRangeParams): GlobalDateRange => {
  const explicitFixedRange =
    urlDashboardRange === "fixed" ? parseUrlDateRange(urlStart, urlEnd) : null
  if (explicitFixedRange) {
    return explicitFixedRange
  }
  const explicitRelative = parseRelativeDashboardDateRange(urlDashboardRange)
  if (explicitRelative && parseUrlDateRange(urlStart, urlEnd)) {
    return calculateRelativeDashboardDateRange(explicitRelative)
  }
  if (savedPreferences?.preset) {
    return calculatePresetRange(savedPreferences.preset)
  }

  if (savedPreferences?.relativeConfig) {
    return calculateDynamicRange(
      savedPreferences.relativeConfig.amount,
      savedPreferences.relativeConfig.unit,
    )
  }

  if (savedPreferences?.customRange) {
    return {
      start: new Date(savedPreferences.customRange.start),
      end: new Date(savedPreferences.customRange.end),
    }
  }

  return (
    parseUrlDateRange(urlStart, urlEnd) ?? calculatePresetRange(defaultPreset)
  )
}

export const resolveDashboardRelativeDateRange = ({
  defaultPreset,
  savedPreferences,
  urlDashboardRange,
  urlEnd,
  urlStart,
}: ResolveDashboardDateRangeParams): RelativeDashboardDateRange | null => {
  if (urlDashboardRange === "fixed" && parseUrlDateRange(urlStart, urlEnd)) {
    return null
  }
  const explicitRelative = parseRelativeDashboardDateRange(urlDashboardRange)
  if (explicitRelative && parseUrlDateRange(urlStart, urlEnd)) {
    return explicitRelative
  }
  if (savedPreferences?.preset) {
    return parseRelativeDashboardDateRange(`preset/${savedPreferences.preset}`)
  }
  if (savedPreferences?.relativeConfig) {
    const { amount, unit } = savedPreferences.relativeConfig
    return parseRelativeDashboardDateRange(`dynamic/${amount}/${unit}`)
  }
  if (savedPreferences?.customRange) {
    return null
  }
  if (parseUrlDateRange(urlStart, urlEnd)) {
    return parseRelativeDashboardDateRange(urlDashboardRange)
  }
  return parseRelativeDashboardDateRange(`preset/${defaultPreset}`)
}

export const buildDashboardWidgetConfigurationHref = ({
  currentTab,
  dashboardId,
  organizationSlug,
  widgetId,
  activeDateRange,
  activeRelativeDateRange,
}: BuildWidgetConfigHrefParams): string => {
  const widgetSegment = widgetId ?? "new"
  const basePath = `/${organizationSlug}/dashboard/${dashboardId}/widgets/configure/${widgetSegment}`
  const searchParams = new URLSearchParams()

  if (activeDateRange) {
    searchParams.set("start", activeDateRange.start.toISOString())
    searchParams.set("end", activeDateRange.end.toISOString())
  }

  if (activeRelativeDateRange) {
    searchParams.set(
      "dashboardRange",
      serializeRelativeDashboardDateRange(activeRelativeDateRange),
    )
  } else if (activeDateRange) {
    searchParams.set("dashboardRange", "fixed")
  }

  if (currentTab) {
    searchParams.set("tab", currentTab)
  }

  const queryString = searchParams.toString()
  return queryString ? `${basePath}?${queryString}` : basePath
}

export const buildDashboardHref = ({
  currentTab,
  dashboardId,
  organizationSlug,
  urlEnd,
  urlStart,
  urlDashboardRange,
}: BuildDashboardHrefParams): string => {
  const basePath = `/${organizationSlug}/dashboard/${dashboardId}`
  const searchParams = new URLSearchParams()

  if (urlStart && urlEnd) {
    searchParams.set("start", urlStart)
    searchParams.set("end", urlEnd)
  }

  if (
    urlDashboardRange &&
    (urlDashboardRange === "fixed" ||
      parseRelativeDashboardDateRange(urlDashboardRange))
  ) {
    searchParams.set("dashboardRange", urlDashboardRange)
  }

  if (currentTab) {
    searchParams.set("tab", currentTab)
  }

  const queryString = searchParams.toString()
  return queryString ? `${basePath}?${queryString}` : basePath
}
